import React from 'react';
import {CLANS} from '../../data/clans';
import {DISCIPLINES_BY_ID} from '../../data/disciplines';
import {PREDATOR_TYPES, PREDATORS_BY_ID, getPredatorDisciplines, isPredatorAllowedForClan} from '../../data/predators';
import {SKILLS_BY_ID, localized} from '../../data/traits';
import {applyClan, applyPredator} from '../../creation';
import {vtmT} from '../../i18n';
import {formatPool} from '../SheetParts';
import {TextField} from './CreationParts';

const disciplineNames = (ids, lng, separator = ' · ') => ids.map(id => localized(DISCIPLINES_BY_ID[id]?.name, lng)).join(separator);

/** Ligne étiquetée d'une carte : « CHASSE   Charisme + Subterfuge ». */
const CardRow = ({label, children}) => (
  <span className="vtmCardRow">
    <span className="vtmCardLabel">{label}</span>
    <span className="vtmCardValue">{children}</span>
  </span>
);

export const ClanStep = ({sheet, change, lng}) => (
  <div className="vtmCardGrid" role="radiogroup" aria-label={vtmT('step_clan')}>
    {CLANS.map(clan => {
      const selected = sheet.clan === clan.id;
      return (
        <button
          key={clan.id}
          type="button"
          role="radio"
          aria-checked={selected}
          className={`vtmChoiceCard${selected ? ' selected' : ''}`}
          onClick={() => change(applyClan(sheet, clan.id))}
        >
          <span className="vtmChoiceTitle">{localized(clan.name, lng)}</span>
          <CardRow label={vtmT('disciplinesShort')}>
            {clan.anyDisciplines ? vtmT('anyDisciplines') : disciplineNames(clan.disciplines, lng)}
          </CardRow>
          <span className="vtmCardDivider" aria-hidden="true" />
          <CardRow label={vtmT('bane')}>{localized(clan.bane, lng)}</CardRow>
          {clan.compulsion && <CardRow label={vtmT('compulsion')}>{localized(clan.compulsion, lng)}</CardRow>}
        </button>
      );
    })}
  </div>
);

const signed = (value) => (value > 0 ? `+${value}` : `−${Math.abs(value)}`);

const PredatorDetails = ({sheet, change, lng}) => {
  const predator = PREDATORS_BY_ID[sheet.predatorType];
  if (!predator) return null;
  const specialty = sheet.specialties.find(item => item.source === 'predator') || {};
  const disciplines = getPredatorDisciplines(predator.id, sheet.clan);
  const reapply = (patch) => change(applyPredator(sheet, {
    predatorId: predator.id,
    specialty: {skill: specialty.skill, name: specialty.name},
    discipline: sheet.predatorDiscipline,
    ...patch,
  }));

  return (
    <div className="vtmPredatorDetails">
      <fieldset className="vtmRadioGroup">
        <legend className="vtmFieldLabel">{vtmT('predatorSpecialty')}</legend>
        {predator.specialties.map(option => {
          const id = `vtmPredSpec-${option.skill}`;
          return (
            <label key={id} htmlFor={id} className="vtmRadio">
              <input
                id={id}
                type="radio"
                name="vtmPredatorSpecialty"
                checked={specialty.skill === option.skill}
                onChange={() => reapply({specialty: {skill: option.skill, name: localized(option.name, lng)}})}
              />
              <span>{localized(SKILLS_BY_ID[option.skill]?.name, lng)} <em>({localized(option.name, lng)})</em></span>
            </label>
          );
        })}
      </fieldset>
      <TextField
        id="vtmPredatorSpecialtyName"
        label={vtmT('predatorSpecialtyName')}
        value={specialty.name}
        onChange={name => reapply({specialty: {skill: specialty.skill, name}})}
      />
      <fieldset className="vtmRadioGroup">
        <legend className="vtmFieldLabel">{vtmT('predatorDiscipline')}</legend>
        {disciplines.map(id => {
          const inputId = `vtmPredDisc-${id}`;
          return (
            <label key={id} htmlFor={inputId} className="vtmRadio">
              <input
                id={inputId}
                type="radio"
                name="vtmPredatorDiscipline"
                checked={sheet.predatorDiscipline === id}
                onChange={() => reapply({discipline: id})}
              />
              <span>{localized(DISCIPLINES_BY_ID[id]?.name, lng)}</span>
            </label>
          );
        })}
      </fieldset>
      {predator.advantageChoice && (
        <p className="vtmHint"><strong>{vtmT('predatorChoice')}</strong> : {localized(predator.advantageChoice, lng)}</p>
      )}
    </div>
  );
};

export const PredatorStep = ({sheet, change, lng}) => {
  if (!sheet.clan) return <p className="vtmHint">{vtmT('chooseClanFirst')}</p>;
  const select = (predator) => {
    const firstSpecialty = predator.specialties[0];
    change(applyPredator(sheet, {
      predatorId: predator.id,
      specialty: {skill: firstSpecialty.skill, name: localized(firstSpecialty.name, lng)},
      discipline: getPredatorDisciplines(predator.id, sheet.clan)[0],
    }));
  };
  return (
    <>
      <div className="vtmCardGrid" role="radiogroup" aria-label={vtmT('step_predator')}>
        {PREDATOR_TYPES.map(predator => {
          const allowed = isPredatorAllowedForClan(predator.id, sheet.clan);
          const selected = sheet.predatorType === predator.id;
          const badges = [
            predator.humanity && vtmT('humanityChange', {value: signed(predator.humanity)}),
            predator.bloodPotency && vtmT('bloodPotencyChange', {value: signed(predator.bloodPotency)}),
          ].filter(Boolean);
          const disciplines = getPredatorDisciplines(predator.id, sheet.clan);
          return (
            <button
              key={predator.id}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!allowed}
              className={`vtmChoiceCard${selected ? ' selected' : ''}`}
              onClick={() => !selected && select(predator)}
            >
              <span className="vtmChoiceTitle">{localized(predator.name, lng)}</span>
              {predator.huntPool.length > 0 && (
                <CardRow label={vtmT('huntPool')}>
                  {predator.huntPool.map(pool => formatPool(pool, lng)).join(` ${vtmT('or')} `)}
                </CardRow>
              )}
              {disciplines.length > 0 && (
                <CardRow label={vtmT('disciplineShort')}>{disciplineNames(disciplines, lng, ` ${vtmT('or')} `)}</CardRow>
              )}
              {(predator.advantages.length > 0 || badges.length > 0) && <span className="vtmCardDivider" aria-hidden="true" />}
              {predator.advantages.map(item => (
                <span key={item.name.fr} className={`vtmCardTrait ${item.kind}`}>
                  <span>{localized(item.name, lng)}</span>
                  <span className="vtmCardTraitDots" aria-label={`${item.level}`}>{'●'.repeat(item.level)}</span>
                </span>
              ))}
              {badges.length > 0 && (
                <span className="vtmCardBadges">
                  {badges.map(badge => <span key={badge} className="vtmCardBadge">{badge}</span>)}
                </span>
              )}
              {!allowed && <span className="vtmCardWarning">{vtmT('notForClan')}</span>}
            </button>
          );
        })}
      </div>
      <PredatorDetails sheet={sheet} change={change} lng={lng} />
    </>
  );
};
